import type { SelfContainedCommand, CommandExecutionContext } from 'types/commands';
import type { StandardToolResult } from 'types/standardToolResults';
import { createSuccessResult, createErrorResult } from 'types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

const log = createFileLogger('RULE_CREATE');

export const ruleCreateCommand: SelfContainedCommand = {
  id: 'rule_create',
  description: '🛡️ CREATE: Create a new AI behavior rule and open it for editing.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Display name for the rule (e.g. "Always reply in Spanish")',
        required: false,
        defaultValue: 'New Rule',
      },
      {
        name: 'instruction',
        type: 'string',
        description: 'The instruction text the AI will follow',
        required: false,
        defaultValue: '',
      },
      {
        name: 'scope',
        type: 'string',
        description: '"user" (all spaces), "space" (this space only), or "document"',
        required: false,
        defaultValue: 'user',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space to create the rule in',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder to place the rule in',
        required: false,
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to open the rule editor after creation. Default true.',
        required: false,
        defaultValue: true,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'Always reply concisely', instruction: 'Keep all responses under 3 sentences.' },
        expectedOutput: createSuccessResult('Rule created', { ruleId: 'rule-123' }),
        description: 'Create a simple user-level rule',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      name = 'New Rule',
      instruction = '',
      scope = 'user',
      spaceId: providedSpaceId,
      folderId,
      createWindow = true,
    } = params;

    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.log('[rule_create] Creating rule:', { name, scope, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newRule = await entityStore.actions.createEntity({
        entityType: 'Rule',
        name: name.trim(),
        spaceId,
        skeleton: {
          '@type': 'Rule',
          instruction,
          enabled: true,
          scope,
          scopeId: scope === 'space' ? spaceId : null,
          scopeName: null,
        },
      });

      const ruleId = newRule._id || newRule.id;

      // Register in dataspace tree so it appears in the sidebar
      if (ruleId && spaceId) {
        try {
          const parentId = folderId || spaceId;
          const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          treeStore._applyAddNode(`${parentId}:${ruleId}`, {
            id: ruleId,
            parentId,
            pos: 'M',
            expanded: false,
            kind: 'canonical',
          });
        } catch (treeErr) {
          log.warn('[rule_create] Tree registration failed (non-fatal):', treeErr);
        }
      }

      if (createWindow !== false && ruleId) {
        try {
          const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          await executeCommand('space_open_window', { entityId: ruleId });
        } catch (windowErr) {
          log.warn('[rule_create] Failed to open window (non-fatal):', windowErr);
        }
      }

      log.log('[rule_create] Rule created:', ruleId);

      return createSuccessResult(`Rule created: "${name.trim()}"`, {
        entityId: ruleId,
        ruleId,
        name: name.trim(),
      });
    } catch (err: any) {
      log.error('[rule_create] Failed:', err);
      return createErrorResult('Failed to create rule', err.message || 'Unknown error', 'execution');
    }
  },
};

// Register so the sidebar "New" menu includes Rule
entityCreationRegistry.register({
  commandId: 'rule_create',
  entityType: 'Rule',
  displayName: 'Rule',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'ShieldCheck',
    phosphorWeight: 'duotone',
  },
  description: 'Create an AI behavior rule',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 10,
});
